import type {
  AiKeysView,
  ContactRequest,
  ConversationView,
  FeedbackRequest,
  IntegrationsRequest,
  JevKeyRequest,
  JobView,
  KnowledgeBaseUpdate,
  LlmKeysRequest,
  ModelListRequest,
  SearchRequest,
  TicketConfig,
} from "@dynamic-rag/contracts";

type FeedbackRating = FeedbackRequest["rating"];
import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";

import type { DashboardApiQuery, TicketsApiQuery } from "../features/dashboard/params";
import { useIsAdmin } from "./auth";
import { api, unwrap } from "./client";

export const keys = {
  system: ["system"] as const,
  settings: ["settings"] as const,
  aiKeys: ["settings", "ai-keys"] as const,
  kbs: ["kbs"] as const,
  kb: (kbId: string) => ["kbs", kbId] as const,
  jobs: (filter: { active?: boolean; kbId?: string; history?: boolean }) => ["jobs", filter] as const,
  job: (jobId: string) => ["job", jobId] as const,
  chunks: (kbId: string, docId: string | null, limit: number) =>
    ["chunks", kbId, docId, limit] as const,
  health: ["health"] as const,
  chatConfig: ["chat", "config"] as const,
  suggestions: ["chat", "suggestions"] as const,
  conversations: ["conversations"] as const,
  conversation: (id: string) => ["conversations", id] as const,
  dashboard: (query: DashboardApiQuery) => ["dashboard", query] as const,
  dashboardTickets: (query: TicketsApiQuery) => ["dashboard", "tickets", query] as const,
  dashboardBusiest: (query: DashboardApiQuery) => ["dashboard", "busiest", query] as const,
};

/** After anything that changes knowledge bases, refresh every view that shows them. */
export function refreshKnowledge(client: QueryClient): void {
  void client.invalidateQueries({ queryKey: keys.kbs });
  void client.invalidateQueries({ queryKey: ["jobs"] });
  void client.invalidateQueries({ queryKey: ["chunks"] });
  void client.invalidateQueries({ queryKey: keys.system });
}

// The deployment ----------------------------------------------------------------------------

/** What the server fixes: the services from .env, development uploads and the support teams.
 * It changes only with a restart, apart from the knowledge bases the source lists. The admin's
 * only, like health and the indexing jobs: a user's pages never ask. */
export function useSystem() {
  const admin = useIsAdmin();
  return useQuery({
    queryKey: keys.system,
    queryFn: () => unwrap(api.GET("/api/v1/system")),
    staleTime: 60_000,
    enabled: admin,
  });
}

// Settings --------------------------------------------------------------------------------

export function useSettings() {
  return useQuery({
    queryKey: keys.settings,
    queryFn: () => unwrap(api.GET("/api/v1/settings")),
  });
}

// Knowledge bases ------------------------------------------------------------------------

export function useKnowledgeBases() {
  return useQuery({
    queryKey: keys.kbs,
    queryFn: () => unwrap(api.GET("/api/v1/knowledge-bases")),
  });
}

export function useKnowledgeBase(kbId: string) {
  return useQuery({
    queryKey: keys.kb(kbId),
    queryFn: () =>
      unwrap(api.GET("/api/v1/knowledge-bases/{kb_id}", { params: { path: { kb_id: kbId } } })),
  });
}

export function useUpdateKnowledgeBase() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ kbId, body }: { kbId: string; body: KnowledgeBaseUpdate }) =>
      unwrap(
        api.PUT("/api/v1/knowledge-bases/{kb_id}", { params: { path: { kb_id: kbId } }, body }),
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.kbs });
    },
  });
}

export function useDeleteKnowledgeBase() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (kbId: string) =>
      unwrap(api.DELETE("/api/v1/knowledge-bases/{kb_id}", { params: { path: { kb_id: kbId } } })),
    onSuccess: () => refreshKnowledge(client),
  });
}

export function useDeleteDocument() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (docId: string) =>
      unwrap(api.DELETE("/api/v1/documents/{doc_id}", { params: { path: { doc_id: docId } } })),
    onSuccess: () => refreshKnowledge(client),
  });
}

export function useChunks(kbId: string, docId: string | null, limit: number) {
  return useQuery({
    queryKey: keys.chunks(kbId, docId, limit),
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/knowledge-bases/{kb_id}/chunks", {
          params: { path: { kb_id: kbId }, query: { doc_id: docId, limit, offset: 0 } },
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

// Jobs ------------------------------------------------------------------------------------

const TERMINAL = new Set<JobView["status"]>(["SUCCEEDED", "FAILED", "CANCELLED"]);

export function isTerminal(job: Pick<JobView, "status">): boolean {
  return TERMINAL.has(job.status);
}

/** Polls quickly while documents are being indexed, and stops when the queue is empty. */
export function useActiveJobs() {
  const admin = useIsAdmin();
  return useQuery({
    queryKey: keys.jobs({ active: true }),
    queryFn: () => unwrap(api.GET("/api/v1/kb/jobs", { params: { query: { active: true } } })),
    refetchInterval: (query) => ((query.state.data?.length ?? 0) > 0 ? 900 : false),
    enabled: admin,
  });
}

export function useRecentJobs(kbId?: string) {
  return useQuery({
    queryKey: keys.jobs({ kbId }),
    queryFn: () =>
      unwrap(api.GET("/api/v1/kb/jobs", { params: { query: { kb_id: kbId ?? null, limit: 12 } } })),
    refetchInterval: (query) => (query.state.data?.some((j) => !isTerminal(j)) ? 1_200 : false),
  });
}

/** The latest jobs (up to 100), for activity over time on the dashboard. */
export function useJobHistory() {
  return useQuery({
    queryKey: keys.jobs({ history: true }),
    queryFn: () => unwrap(api.GET("/api/v1/kb/jobs", { params: { query: { limit: 100 } } })),
    refetchInterval: (query) => (query.state.data?.some((j) => !isTerminal(j)) ? 2_000 : false),
  });
}

/** Follows one job until it ends (KB-10). */
export function useJob(jobId: string | null | undefined) {
  return useQuery({
    queryKey: keys.job(jobId ?? "none"),
    queryFn: () =>
      unwrap(api.GET("/api/v1/kb/jobs/{job_id}", { params: { path: { job_id: jobId ?? "" } } })),
    enabled: Boolean(jobId),
    // Until the job ends; a request that failed (the job was deleted, the API is down) is not
    // asked again on the interval.
    refetchInterval: (query) =>
      query.state.status === "error" || (query.state.data && isTerminal(query.state.data)) ? false : 800,
  });
}

// Search and health ----------------------------------------------------------------

export function useSearch() {
  return useMutation({
    mutationFn: (body: SearchRequest) => unwrap(api.POST("/api/v1/search", { body })),
  });
}

export function useHealth() {
  const admin = useIsAdmin();
  return useQuery({
    queryKey: keys.health,
    queryFn: () => unwrap(api.GET("/api/v1/health")),
    refetchInterval: 30_000,
    enabled: admin,
  });
}

export function useVendorHealth() {
  return useMutation({
    mutationFn: () => unwrap(api.POST("/api/v1/health/vendors")),
  });
}

// Ticket and connection settings ------------------------------------------------------------

function useSettingsMutation<Body, Result>(send: (body: Body) => Promise<Result>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: send,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.settings });
    },
  });
}

export function useSaveTickets() {
  return useSettingsMutation((body: TicketConfig) =>
    unwrap(api.PUT("/api/v1/settings/tickets", { body })),
  );
}

export function useSaveIntegrations() {
  return useSettingsMutation((body: IntegrationsRequest) =>
    unwrap(api.PUT("/api/v1/settings/integrations", { body })),
  );
}

// AI keys: the LLM key and models and the JEV key ------------------------------------------

export function useAiKeys() {
  return useQuery({
    queryKey: keys.aiKeys,
    queryFn: () => unwrap(api.GET("/api/v1/settings/ai-keys")),
  });
}

/** The chat models a key can use, read live from its provider (this also proves the key). */
export function useListModels() {
  return useMutation({
    mutationFn: (body: ModelListRequest) =>
      unwrap(api.POST("/api/v1/settings/ai-keys/llm/models", { body })),
  });
}

/** Every change returns the keys now in use; the services shown elsewhere follow. */
function useAiKeysMutation<Body>(send: (body: Body) => Promise<AiKeysView>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: send,
    onSuccess: (view) => {
      client.setQueryData(keys.aiKeys, view);
      void client.invalidateQueries({ queryKey: keys.system });
      void client.invalidateQueries({ queryKey: keys.health });
    },
  });
}

export function useSaveLlmKeys() {
  return useAiKeysMutation((body: LlmKeysRequest) =>
    unwrap(api.PUT("/api/v1/settings/ai-keys/llm", { body })),
  );
}

export function useResetLlmKeys() {
  return useAiKeysMutation((_: void) => unwrap(api.DELETE("/api/v1/settings/ai-keys/llm")));
}

export function useSaveJevKey() {
  return useAiKeysMutation((body: JevKeyRequest) =>
    unwrap(api.PUT("/api/v1/settings/ai-keys/jev", { body })),
  );
}

export function useResetJevKey() {
  return useAiKeysMutation((_: void) => unwrap(api.DELETE("/api/v1/settings/ai-keys/jev")));
}

// Chat and conversation history -------------------------------------------------------------

export function useChatConfig() {
  return useQuery({
    queryKey: keys.chatConfig,
    queryFn: () => unwrap(api.GET("/api/v1/chat/config")),
    staleTime: 60_000,
  });
}

/** Questions for the welcome screen; the server checks each against the knowledge base. */
export function useChatSuggestions() {
  return useQuery({
    queryKey: keys.suggestions,
    queryFn: () => unwrap(api.GET("/api/v1/chat/suggestions")),
    staleTime: 5 * 60_000,
  });
}

/** Rate an answer as helpful or not, or remove the vote (rating null). The conversation shows
 * the vote at once and goes back if the server refuses it. */
export function useRateMessage(conversationId: string) {
  const client = useQueryClient();
  const key = keys.conversation(conversationId);
  return useMutation({
    mutationFn: ({ messageId, rating }: { messageId: string; rating: FeedbackRating | null }) => {
      const params = { path: { conversation_id: conversationId, message_id: messageId } };
      const url = "/api/v1/conversations/{conversation_id}/messages/{message_id}/feedback";
      return rating === null
        ? unwrap(api.DELETE(url, { params }))
        : unwrap(api.PUT(url, { params, body: { rating } }));
    },
    onMutate: async ({ messageId, rating }) => {
      await client.cancelQueries({ queryKey: key });
      const before = client.getQueryData<ConversationView>(key);
      if (before) {
        client.setQueryData<ConversationView>(key, {
          ...before,
          messages: before.messages.map((m) => (m.message_id === messageId ? { ...m, feedback: rating } : m)),
        });
      }
      return { before };
    },
    onError: (_error, _vars, context) => {
      if (context?.before) client.setQueryData(key, context.before);
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: key });
    },
  });
}

export function useConversations() {
  return useQuery({
    queryKey: keys.conversations,
    queryFn: () => unwrap(api.GET("/api/v1/conversations", { params: { query: { limit: 50 } } })),
  });
}

/** Query options for one conversation, shared by the hook and by code that refreshes it. */
export function conversationOptions(id: string) {
  return queryOptions({
    queryKey: keys.conversation(id),
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/conversations/{conversation_id}", {
          params: { path: { conversation_id: id } },
        }),
      ),
  });
}

/** One conversation. Polls every few seconds while its last question waits for an answer the
 * page is not following itself (`awaitAnswer`), and slowly while a handoff in it may still
 * change status (HO-06). */
export function useConversation(id: string | null, { awaitAnswer = true }: { awaitAnswer?: boolean } = {}) {
  return useQuery({
    ...conversationOptions(id ?? "none"),
    enabled: Boolean(id),
    refetchInterval: (query) =>
      awaitAnswer && awaitingAnswer(query.state.data)
        ? 2_500
        : hasLiveSupport(query.state.data)
          ? 30_000
          : false,
  });
}

// A question waits for its answer as long as a run may take: five minutes on the server
// (RUN_TIMEOUT_S in rag_core.agent.service), and a margin.
const ANSWER_WAIT_MS = 6 * 60_000;

/** The last message is the user's, sent less than a run's time ago, and its answer is not
 * stored yet: the page was left, or reloaded, while it was being answered. */
export function awaitingAnswer(view: ConversationView | undefined): boolean {
  const last = view?.messages.at(-1);
  return last?.role === "user" && Date.now() - Date.parse(last.created_at) < ANSWER_WAIT_MS;
}

function hasLiveSupport(view: ConversationView | undefined): boolean {
  return Boolean(
    view?.messages.some(
      (m) => m.escalation && !["RESOLVED", "FALLBACK", "FAILED"].includes(m.escalation.status),
    ),
  );
}

export function useCancelWorkflow() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (workflowId: string) =>
      unwrap(
        api.POST("/api/v1/chat/workflows/{workflow_id}/cancel", {
          params: { path: { workflow_id: workflowId } },
        }),
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.conversations });
    },
  });
}

export function useDeleteConversation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.DELETE("/api/v1/conversations/{conversation_id}", {
          params: { path: { conversation_id: id } },
        }),
      ),
    onSuccess: (_, id) => {
      client.removeQueries({ queryKey: keys.conversation(id) });
      void client.invalidateQueries({ queryKey: keys.conversations });
    },
  });
}

/** Contact details for a security or urgent request, raised before any form. */
export function useAddContact(conversationId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, body }: { requestId: string; body: ContactRequest }) =>
      unwrap(
        api.PUT("/api/v1/conversations/{conversation_id}/support-requests/{request_id}/contact", {
          params: { path: { conversation_id: conversationId, request_id: requestId } },
          body,
        }),
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.conversation(conversationId) });
    },
  });
}

/** The Dashboard for the page's filters and the ticket filters. It refreshes every minute,
 * and keeps showing the last numbers while new ones load. */
export function useDashboard(query: DashboardApiQuery) {
  return useQuery({
    queryKey: keys.dashboard(query),
    queryFn: () => unwrap(api.GET("/api/v1/dashboard", { params: { query } })),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
}

/** The busiest hours for days chosen on their card; only asked while such days are chosen. */
export function useDashboardBusiest(query: DashboardApiQuery | null) {
  return useQuery({
    queryKey: keys.dashboardBusiest(query ?? {}),
    queryFn: () => unwrap(api.GET("/api/v1/dashboard/busiest", { params: { query: query ?? {} } })),
    enabled: query !== null,
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
}

/** One page of the tickets the filters keep, in the order asked; refreshed with the page. */
export function useDashboardTickets(query: TicketsApiQuery) {
  return useQuery({
    queryKey: keys.dashboardTickets(query),
    queryFn: () => unwrap(api.GET("/api/v1/dashboard/tickets", { params: { query } })),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
}
